import { useEffect, useSyncExternalStore } from "react";

/**
 * Theme mode and palette (v2.1 T2.4 / T2.5, D-51).
 *
 * Two attributes on <html>, nothing else: `data-theme` (light | dark — the RESOLVED mode, so CSS never
 * has to know about "system") and `data-palette` (default | sapphire | violet | gold | emerald | pink).
 *
 * The palette has two sources. The workspace's choice lives in `cv_settings.appearance` and is what
 * everyone sees; a person may override it for themselves, and that override lives in this browser's
 * localStorage only — it is a preference about their screen, not a fact about the business.
 *
 * A tiny external store rather than React context, so the sidebar footer, Settings → Appearance and
 * the charts all read one value without a provider having to wrap the router.
 */
export const PALETTES = ["default", "sapphire", "violet", "gold", "emerald", "pink"] as const;
export type Palette = (typeof PALETTES)[number];
export type ThemeMode = "light" | "system" | "dark";

/** The swatch each palette shows: its light-mode primary. */
export const SWATCH: Record<Palette, string> = {
  default: "hsl(243 75% 59%)",
  sapphire: "hsl(217 91% 60%)",
  violet: "hsl(262 83% 58%)",
  gold: "hsl(38 92% 50%)",
  emerald: "hsl(160 84% 39%)",
  pink: "hsl(330 81% 60%)",
};

const MODE_KEY = "cornerstone-theme-mode";
const OVERRIDE_KEY = "cornerstone-palette";
const WORKSPACE_KEY = "cornerstone-workspace-palette";
const LEGACY_KEY = "landcrm-theme";

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Private windows and blocked storage: the choice still applies for this page view.
  }
}

export function isPalette(value: unknown): value is Palette {
  return typeof value === "string" && (PALETTES as readonly string[]).includes(value);
}

function initialMode(): ThemeMode {
  const stored = read(MODE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") return stored;
  // v2.0 stored an explicit light/dark under the old key; honour it once rather than flip people.
  const legacy = read(LEGACY_KEY);
  if (legacy === "light" || legacy === "dark") return legacy;
  return "system";
}

interface State {
  mode: ThemeMode;
  override: Palette | null;
  workspace: Palette;
}

let state: State = {
  mode: initialMode(),
  override: ((p) => (isPalette(p) ? p : null))(read(OVERRIDE_KEY)),
  workspace: ((p) => (isPalette(p) ? p : "default"))(read(WORKSPACE_KEY)),
};
const listeners = new Set<() => void>();

function systemDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function resolvedMode(mode: ThemeMode = state.mode): "light" | "dark" {
  if (mode === "system") return systemDark() ? "dark" : "light";
  return mode;
}

export function activePalette(): Palette {
  return state.override ?? state.workspace;
}

function apply(): void {
  const html = document.documentElement;
  const dark = resolvedMode() === "dark";
  html.setAttribute("data-theme", dark ? "dark" : "light");
  html.classList.toggle("dark", dark);
  html.setAttribute("data-palette", activePalette());
}

function emit(next: Partial<State>): void {
  state = { ...state, ...next };
  apply();
  listeners.forEach((l) => l());
}

export const theme = {
  setMode(mode: ThemeMode) {
    write(MODE_KEY, mode);
    emit({ mode });
  },
  /** This person's own palette; `null` goes back to the workspace's. */
  setOverride(palette: Palette | null) {
    write(OVERRIDE_KEY, palette);
    emit({ override: palette });
  },
  /** The workspace palette as the server reports it (cached so the next load paints it at once). */
  setWorkspace(palette: Palette) {
    write(WORKSPACE_KEY, palette);
    emit({ workspace: palette });
  },
  apply,
};

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme(): State & { resolved: "light" | "dark"; palette: Palette } {
  const snapshot = useSyncExternalStore(subscribe, () => state);
  // "System" has to follow the OS live, not only at load.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if (state.mode === "system") emit({});
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return { ...snapshot, resolved: resolvedMode(snapshot.mode), palette: snapshot.override ?? snapshot.workspace };
}
